import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { ChevronRight, ChevronDown, BookOpen, CheckCircle, PenTool } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Shared trail marker data
const TRAIL_STEPS = [
  { key: 'GIT_101', label: 'The GIT', short: 'GIT' },
  { key: 'BLUEPRINTS', label: 'Blueprints', short: 'BP' },
  { key: 'RECAPITULATION', label: 'Recapitulation', short: 'REC' },
  { key: 'CORNER_1', label: 'Corner #1', short: 'C1' },
  { key: 'CORNER_2', label: 'Corner #2', short: 'C2' },
  { key: 'CORNER_3', label: 'Corner #3', short: 'C3' },
  { key: 'CORNER_4', label: 'Corner #4', short: 'C4' },
];

// Trail Marker Component
export function TrailMarker({ progress }) {
  return (
    <div className="flex items-center justify-center gap-1 py-6 overflow-x-auto" data-testid="trail-marker">
      {TRAIL_STEPS.map((step, i) => {
        const done = progress?.[step.key]?.status === 'completed';
        const active = progress?.[step.key]?.status === 'in_progress';
        return (
          <div key={step.key} className="flex items-center">
            <div className="flex flex-col items-center">
              <div className="w-7 h-7 rounded-full flex items-center justify-center border transition-all"
                style={{
                  background: done ? 'rgba(16,185,129,0.15)' : active ? 'rgba(212,165,74,0.1)' : 'rgba(10,7,3,0.6)',
                  borderColor: done ? 'rgba(16,185,129,0.4)' : active ? 'rgba(212,165,74,0.3)' : 'rgba(120,80,20,0.15)',
                }}>
                {done ? <CheckCircle className="w-3.5 h-3.5" style={{ color: '#10b981' }} />
                  : <span className="text-[8px] font-bold" style={{ color: active ? '#d4a54a' : 'rgba(120,80,20,0.3)' }}>{step.short}</span>}
              </div>
              <span className="text-[7px] mt-1 hidden sm:block" style={{ color: done ? '#10b981' : active ? '#d4a54a' : 'rgba(120,80,20,0.25)' }}>{step.label}</span>
            </div>
            {i < TRAIL_STEPS.length - 1 && (
              <div className="w-4 sm:w-8 h-px mx-0.5" style={{ background: done ? 'rgba(16,185,129,0.3)' : 'rgba(120,80,20,0.1)' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// Pieces In Place — 4-corner puzzle graphic
export function PiecesInPlace({ progress }) {
  const corners = ['CORNER_1', 'CORNER_2', 'CORNER_3', 'CORNER_4'];
  const done = corners.filter(c => progress?.[c]?.status === 'completed').length;
  const allDone = done === 4;
  return (
    <div className="flex flex-col items-center py-6" data-testid="pieces-in-place">
      <p className="text-[9px] tracking-widest uppercase mb-3" style={{ color: allDone ? '#10b981' : 'rgba(120,80,20,0.4)' }}>
        {allDone ? 'FRAME COMPLETE' : `${done}/4 CORNERS IN PLACE`}
      </p>
      <div className="grid grid-cols-2 gap-1.5">
        {corners.map((c, i) => {
          const filled = progress?.[c]?.status === 'completed';
          return (
            <div key={c} className="w-10 h-10 rounded-lg flex items-center justify-center border transition-all"
              style={{
                background: filled ? 'rgba(212,165,74,0.15)' : 'rgba(10,7,3,0.5)',
                borderColor: filled ? 'rgba(212,165,74,0.4)' : 'rgba(120,80,20,0.1)',
              }}>
              <span className="text-[10px] font-bold" style={{ color: filled ? '#d4a54a' : 'rgba(120,80,20,0.15)' }}>#{i + 1}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// GIT Interactive Panel
export function GITPanel({ verse, verseRef, anchorPhrase, answers }) {
  const [revealed, setRevealed] = useState({});
  const theme = useTheme();
  const toggle = (key) => setRevealed(prev => ({ ...prev, [key]: !prev[key] }));

  // Highlight anchor phrase in verse text
  const highlightVerse = (text, anchor) => {
    if (!anchor) return text;
    const idx = text.toLowerCase().indexOf(anchor.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span style={{ color: theme.accentPrimary, fontWeight: 'bold', textDecoration: 'underline', textDecorationColor: `${theme.accentPrimary}66`, textUnderlineOffset: '4px' }}>{text.slice(idx, idx + anchor.length)}</span>
        {text.slice(idx + anchor.length)}
      </>
    );
  };

  return (
    <div className="mb-12" data-testid="git-panel">
      {/* Verse Display */}
      <div className="rounded-xl border p-6 mb-6" style={{ background: theme.scriptureBg, borderColor: theme.scriptureBorder }}>
        <p className="text-xs tracking-widest uppercase mb-4" style={{ color: theme.labelColor }}>THE TEXT</p>
        <blockquote className="text-base italic leading-relaxed mb-3" style={{ color: theme.scriptureText }}>
          {highlightVerse(verse, anchorPhrase)}
        </blockquote>
        <p className="text-sm text-right" style={{ color: theme.textSecondary }}>— {verseRef}</p>
      </div>

      {/* Anchor Phrase Callout */}
      <div className="rounded-xl border p-5 mb-6" style={{ background: theme.accentBg, borderColor: theme.accentBorder }}>
        <p className="text-[9px] tracking-widest uppercase mb-2" style={{ color: theme.labelColor }}>ANCHOR PHRASE</p>
        <p className="text-xl font-bold" style={{ color: theme.accentPrimary }}>"{anchorPhrase}"</p>
      </div>

      {/* Three GIT Questions — Click to reveal */}
      <p className="text-xs tracking-widest uppercase mb-4" style={{ color: theme.labelColor }}>APPLY THE GIT</p>
      <div className="space-y-3">
        {answers.map((a, i) => (
          <button key={i} onClick={() => toggle(i)}
            className="w-full text-left rounded-xl border p-5 transition-all"
            style={{
              background: revealed[i] ? theme.scriptureBg : theme.bgCard,
              borderColor: revealed[i] ? theme.scriptureBorder : theme.borderSecondary,
            }}
            data-testid={`git-answer-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-base font-bold" style={{ color: theme.accentPrimary }}>{a.q}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${revealed[i] ? 'rotate-180' : ''}`} style={{ color: theme.navText }} />
            </div>
            {revealed[i] && (
              <p className="mt-3 text-sm leading-relaxed" style={{ color: theme.textPrimary }}>{a.a}</p>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// Three-Voice Teaching Section
export function ThreeVoices({ teacher, professor, pastoral }) {
  const [activeVoice, setActiveVoice] = useState('teacher');
  const theme = useTheme();
  const voices = [
    { key: 'teacher', label: 'Teacher', desc: 'Plain & Direct', color: theme.accentPrimary, content: teacher },
    { key: 'professor', label: 'Professor', desc: 'Scholarly', color: '#6b9bd2', content: professor },
    { key: 'pastoral', label: 'Pastoral', desc: 'Heart', color: theme.successColor, content: pastoral },
  ];

  return (
    <div className="mb-12" data-testid="three-voices">
      <p className="text-xs tracking-widest uppercase mb-4" style={{ color: theme.labelColor }}>THREE VOICES</p>
      <div className="flex gap-2 mb-6">
        {voices.map(v => (
          <button key={v.key} onClick={() => setActiveVoice(v.key)}
            className="flex-1 py-2.5 rounded-lg text-center border transition-all"
            style={{
              background: activeVoice === v.key ? `${v.color}15` : theme.bgCard,
              borderColor: activeVoice === v.key ? `${v.color}40` : theme.borderSecondary,
            }}
            data-testid={`voice-${v.key}`}>
            <p className="text-xs font-bold" style={{ color: activeVoice === v.key ? v.color : theme.textSecondary }}>{v.label}</p>
            <p className="text-[9px]" style={{ color: activeVoice === v.key ? `${v.color}99` : theme.textMuted }}>{v.desc}</p>
          </button>
        ))}
      </div>
      {voices.map(v => v.key === activeVoice && (
        <div key={v.key} className="rounded-xl border p-6" style={{ background: `${v.color}06`, borderColor: `${v.color}20` }}>
          <p className="text-base leading-relaxed whitespace-pre-line" style={{ color: theme.textPrimary }}>
            {v.content}
          </p>
        </div>
      ))}
    </div>
  );
}

// Rules Out Callout
export function RulesOut({ text }) {
  const theme = useTheme();
  return (
    <div className="mb-12 rounded-xl border p-6" style={{ background: `${theme.labelColor}08`, borderColor: `${theme.labelColor}20` }} data-testid="rules-out">
      <p className="text-[9px] tracking-widest uppercase mb-3 font-bold" style={{ color: theme.labelColor }}>WHAT THIS RULES OUT</p>
      <p className="text-base leading-relaxed" style={{ color: theme.textPrimary }}>{text}</p>
    </div>
  );
}

// Discussion Questions
export function DiscussionQuestions({ questions }) {
  const theme = useTheme();
  return (
    <div className="mb-12" data-testid="discussion-questions">
      <p className="text-xs tracking-widest uppercase mb-4" style={{ color: theme.labelColor }}>DISCUSSION QUESTIONS</p>
      <div className="space-y-3">
        {questions.map((q, i) => (
          <div key={i} className="flex gap-3 p-4 rounded-xl border" style={{ background: theme.bgCard, borderColor: theme.borderSecondary }}>
            <span className="text-sm font-bold flex-shrink-0" style={{ color: theme.accentPrimary }}>{i + 1}.</span>
            <p className="text-sm leading-relaxed" style={{ color: theme.textPrimary }}>{q}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Dig Deeper Links — fetch from puzzle piece appendix mapping or use manual links
export function DigDeeper({ links, pieceId }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [dynamicLinks, setDynamicLinks] = useState(links || []);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!pieceId) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/puzzle/pieces/${pieceId}/appendices`);
        if (res.ok) {
          const data = await res.json();
          if (data.appendices?.length > 0) {
            setDynamicLinks(data.appendices.map(a => ({
              code: a.appendix_code ? `APP-${a.appendix_code}` : 'APP',
              title: a.chapter_title || a.title,
              bookId: a.book_id,
              chapterNum: a.chapter_order || 1,
            })));
          }
        }
      } catch { /* fallback to static links */ }
    })();
  }, [pieceId, API_URL]);

  if (!dynamicLinks?.length) return null;

  return (
    <div className="mb-12" data-testid="dig-deeper">
      <p className="text-xs tracking-widest uppercase mb-4" style={{ color: theme.labelColor }}>DIG DEEPER — RELATED APPENDICES</p>
      <div className="space-y-2">
        {dynamicLinks.map((link, i) => (
          <button key={i} onClick={() => link.bookId && link.chapterNum ? navigate(`/ebooks/${link.bookId}/${link.chapterNum}`) : null}
            className="w-full flex items-start gap-3 p-4 rounded-xl border transition-all text-left"
            style={{ background: theme.bgCard, borderColor: theme.borderSecondary }}>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0" style={{ background: theme.accentBg, color: theme.accentPrimary, border: `1px solid ${theme.accentBorder}` }}>{link.code}</span>
            <p className="text-sm flex-1" style={{ color: theme.textSecondary }}>{link.title}</p>
            {link.bookId && <svg className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: theme.accentPrimary }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>}
          </button>
        ))}
      </div>
    </div>
  );
}

// Book Reading Hook — chapter preview + link to ebook reader
const MAIN_BOOK_ID = '0e0a3773-ec98-488b-9b8e-69c881877f97';
const APPENDIX_BOOK_ID = '786d9ebc-716f-420d-a2b2-591f97f8e366';

export function BookReadingHook({ chapterNum, chapterTitle, previewText, appendixChapterNum, appendixTitle }) {
  const navigate = useNavigate();
  const theme = useTheme();
  return (
    <div className="mb-12 rounded-xl border overflow-hidden" style={{ borderColor: theme.accentBorder, background: theme.accentBg }} data-testid="book-reading-hook">
      <div className="px-6 py-4 border-b" style={{ borderColor: theme.borderSecondary }}>
        <p className="text-[9px] tracking-[0.3em] uppercase mb-1" style={{ color: theme.labelColor }}>READ IT IN THE BOOK</p>
        <p className="text-base font-bold" style={{ color: theme.accentPrimary, fontFamily: "'Times New Roman', Georgia, serif" }}>{chapterTitle}</p>
      </div>
      <div className="px-6 py-5">
        {previewText && (
          <p className="text-sm italic leading-relaxed mb-5" style={{ color: theme.textSecondary, fontFamily: "'Times New Roman', Georgia, serif" }}>
            "{previewText.length > 200 ? previewText.slice(0, 200) + '...' : previewText}"
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          <button onClick={() => navigate(`/ebooks/${MAIN_BOOK_ID}/${chapterNum}`)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all hover:scale-105 border"
            style={{ background: theme.accentBg, borderColor: theme.accentBorder, color: theme.accentPrimary }}
            data-testid="btn-read-chapter">
            <BookOpen className="w-4 h-4" />
            Read This Chapter
          </button>
          {appendixChapterNum && (
            <button onClick={() => navigate(`/ebooks/${APPENDIX_BOOK_ID}/${appendixChapterNum}`)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all hover:scale-105 border"
              style={{ background: theme.bgCard, borderColor: theme.borderSecondary, color: theme.textSecondary }}
              data-testid="btn-read-appendix">
              <BookOpen className="w-4 h-4" />
              {appendixTitle || 'Read Appendix'}
            </button>
          )}
          <button onClick={() => navigate('/buy')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all hover:scale-105"
            style={{ background: theme.buyBtnBg, color: '#fff' }}
            data-testid="btn-buy-ebook-hook">
            Buy the Full Ebook
          </button>
        </div>
      </div>
    </div>
  );
}

// Corner Lesson Layout (shared wrapper)
export function CornerLessonLayout({ cornerNum, title, moduleKey, nextRoute, nextLabel, children }) {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const theme = useTheme();
  const [completing, setCompleting] = useState(false);
  const [progress, setProgress] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/api/learning/progress`, { headers, credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setProgress(data.progress || {});
        }
      } catch {}
    })();
  }, [token]);

  const handleComplete = useCallback(async () => {
    if (!token && !user) {
      toast.info('Sign in to save your progress and continue');
      sessionStorage.setItem('pendingLessonCompletion', JSON.stringify({ moduleKey, nextRoute }));
      navigate('/login', { state: { from: { pathname: `/lesson/corner${cornerNum}` } } });
      return;
    }
    setCompleting(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${API_URL}/api/learning/progress/${moduleKey}/start`, { method: 'POST', headers, credentials: 'include' }).catch(() => {});
      // Reflection is optional — don't let AI failures block progression
      fetch(`${API_URL}/api/learning/progress/${moduleKey}/reflection`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ reflection: `Completed Corner Piece #${cornerNum} lesson and GIT application.` }),
      }).catch(() => {});
      const res = await fetch(`${API_URL}/api/learning/progress/${moduleKey}/complete`, { method: 'POST', headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const unlocked = data.newly_unlocked || [];
        toast.success(unlocked.length > 0 ? `Corner #${cornerNum} complete! ${unlocked.join(', ')} unlocked.` : `Corner #${cornerNum} complete!`);
        setTimeout(() => navigate(nextRoute), 1500);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not complete');
      }
    } catch (e) { console.error('Completion error:', e); toast.error('Error completing — please try again'); }
    setCompleting(false);
  }, [token, user, navigate, moduleKey, cornerNum, nextRoute]);

  // Auto-complete if returning from login with a pending action
  useEffect(() => {
    const pending = sessionStorage.getItem('pendingLessonCompletion');
    if (pending && (token || user)) {
      try {
        const { moduleKey: pendingKey, nextRoute: pendingRoute } = JSON.parse(pending);
        if (pendingKey === moduleKey) {
          sessionStorage.removeItem('pendingLessonCompletion');
          (async () => {
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            await fetch(`${API_URL}/api/learning/progress/${moduleKey}/start`, { method: 'POST', headers, credentials: 'include' }).catch(() => {});
            fetch(`${API_URL}/api/learning/progress/${moduleKey}/reflection`, {
              method: 'POST', headers, credentials: 'include',
              body: JSON.stringify({ reflection: `Completed Corner Piece #${cornerNum} lesson and GIT application.` }),
            }).catch(() => {});
            const res = await fetch(`${API_URL}/api/learning/progress/${moduleKey}/complete`, { method: 'POST', headers, credentials: 'include' });
            if (res.ok) {
              toast.success(`Corner #${cornerNum} complete!`);
              setTimeout(() => navigate(pendingRoute), 1500);
            }
          })();
        }
      } catch {}
    }
  }, [token, user, navigate, moduleKey, cornerNum]);

  return (
    <div className="min-h-screen" style={{ background: theme.bgPrimary, fontFamily: "'Times New Roman', 'Georgia', serif" }}>
      <nav className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: theme.borderPrimary }}>
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm" style={{ color: theme.navText }} data-testid="back-to-map">
          <ChevronRight className="w-4 h-4 rotate-180" /> Back to the Map
        </button>
        <span className="text-xs tracking-widest uppercase" style={{ color: theme.textMuted }}>Corner Piece #{cornerNum}</span>
        {(user?.email === 'cfenison@gmail.com' || user?.email === 'testadmin@test.com') ? (
          <button onClick={() => navigate(`/admin/lesson-content/${moduleKey}`)} className="flex items-center gap-1.5 text-xs" style={{ color: theme.accentPrimary }} data-testid="btn-edit-content">
            <PenTool className="w-3.5 h-3.5" /> Edit Content
          </button>
        ) : <div className="w-20" />}
      </nav>

      <div className="max-w-3xl mx-auto px-5 py-8">
        <TrailMarker progress={progress} />
        <PiecesInPlace progress={progress} />

        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.5em] uppercase mb-4" style={{ color: theme.labelColor }}>CORNER PIECE #{cornerNum}</p>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: theme.accentPrimary }}>{title}</h1>
        </div>

        {children}

        {/* Completion CTA */}
        <div className="text-center py-8" data-testid="bridge-cta">
          <div className="h-px mx-auto mb-8" style={{ maxWidth: '200px', background: `linear-gradient(90deg, transparent, ${theme.accentPrimary}66, transparent)` }} />
          <button onClick={handleComplete} disabled={completing}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-xl text-base font-bold transition-all border"
            style={{ background: `${theme.accentPrimary}18`, borderColor: `${theme.accentPrimary}66`, color: theme.accentPrimary }}
            data-testid={`btn-complete-corner-${cornerNum}`}>
            <BookOpen className="w-5 h-5" />
            {completing ? 'Marking Complete...' : nextLabel}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}


// Generic Lesson Layout — for Landmarks, Warnings, and future lesson types
export function GenericLessonLayout({ lessonType, lessonNum, title, moduleKey, nextRoute, nextLabel, accentColor, icon, children }) {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const theme = useTheme();
  const [completing, setCompleting] = useState(false);

  const handleComplete = useCallback(async () => {
    if (!token && !user) {
      toast.info('Sign in to save your progress and continue');
      const currentPath = `/lesson/${moduleKey}`;
      sessionStorage.setItem('pendingLessonCompletion', JSON.stringify({ moduleKey, nextRoute }));
      navigate('/login', { state: { from: { pathname: currentPath } } });
      return;
    }
    setCompleting(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${API_URL}/api/learning/progress/${moduleKey}/start`, { method: 'POST', headers, credentials: 'include' }).catch(() => {});
      fetch(`${API_URL}/api/learning/progress/${moduleKey}/reflection`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ reflection: `Completed ${lessonType} #${lessonNum} lesson.` }),
      }).catch(() => {});
      const res = await fetch(`${API_URL}/api/learning/progress/${moduleKey}/complete`, { method: 'POST', headers, credentials: 'include' });
      if (res.ok) {
        toast.success(`${title} complete!`);
        setTimeout(() => navigate(nextRoute), 1500);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not complete');
      }
    } catch (e) { console.error('Completion error:', e); toast.error('Error completing — please try again'); }
    setCompleting(false);
  }, [token, user, navigate, moduleKey, lessonNum, lessonType, nextRoute, title]);

  // Auto-complete on return from login
  useEffect(() => {
    const pending = sessionStorage.getItem('pendingLessonCompletion');
    if (pending && (token || user)) {
      try {
        const { moduleKey: pk, nextRoute: pr } = JSON.parse(pending);
        if (pk === moduleKey) {
          sessionStorage.removeItem('pendingLessonCompletion');
          (async () => {
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            await fetch(`${API_URL}/api/learning/progress/${moduleKey}/start`, { method: 'POST', headers, credentials: 'include' }).catch(() => {});
            const res = await fetch(`${API_URL}/api/learning/progress/${moduleKey}/complete`, { method: 'POST', headers, credentials: 'include' });
            if (res.ok) {
              toast.success(`${title} complete!`);
              setTimeout(() => navigate(pr), 1500);
            }
          })();
        }
      } catch {}
    }
  }, [token, user, navigate, moduleKey, title]);

  const color = accentColor || '#d4a54a';

  return (
    <div className="min-h-screen" style={{ background: theme.bgSecondary, fontFamily: "'Times New Roman', Georgia, serif" }}>
      {/* Nav */}
      <div className="sticky top-0 z-50 px-4 py-3 flex items-center justify-between" style={{ background: theme.bgOverlay, borderBottom: `1px solid ${theme.borderPrimary}` }}>
        <button onClick={() => navigate('/')} className="text-xs flex items-center gap-1" style={{ color: theme.textMuted }}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          Back to Map
        </button>
        <span className="text-xs tracking-widest uppercase" style={{ color }}>{icon} {lessonType} #{lessonNum}</span>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-8">
        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.5em] uppercase mb-4" style={{ color: accentColor === '#dc2626' ? '#dc2626' : color }}>{lessonType?.toUpperCase()} #{lessonNum}</p>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color }}>{title}</h1>
        </div>

        {children}

        {/* Completion CTA */}
        <div className="text-center py-8" data-testid="bridge-cta">
          <div className="h-px mx-auto mb-8" style={{ maxWidth: '200px', background: `linear-gradient(90deg, transparent, ${color}66, transparent)` }} />
          <button onClick={handleComplete} disabled={completing}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-xl text-base font-bold transition-all border"
            style={{ background: `${color}18`, borderColor: `${color}66`, color }}
            data-testid={`btn-complete-${lessonType}-${lessonNum}`}>
            <BookOpen className="w-5 h-5" />
            {completing ? 'Marking Complete...' : nextLabel}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
